import { c as _c } from "react/compiler-runtime";
import { fetchGiftableItemList, fetchItemDetail, fetchItemStoreItemList, fetchItemUse, fetchMyItemList, fetchSendItemGift, fetchSendPush } from '@module/apis/client-side/item';
import type { FetchItemDetailListResponse, FetchItemStoreItemListResponse, FetchMyItemListResponse, GiftableItemList, ItemDetailParams, ItemPageListParams, ItemUseParams, ItemUseResponse, SendItemGiftParams, SendPushParams } from '@module/apis/client-side/item/type';
import { keepPreviousData, type UseInfiniteQueryOptions, type UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import type { ApiBaseResponse } from '../../apis/type';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { itemQueryKeys } from './itemQueryKeys';
export const useFetchItemStoreItemListQuery = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = itemQueryKeys.fetchItemStoreItemList();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0.queryKey,
      queryFn: _temp,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useFetchGiftableItemListQuery = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = itemQueryKeys.fetchGiftableItemList();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0.queryKey,
      queryFn: _temp2,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useFetchItemUseMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = itemQueryKeys.fetchItemUse();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp3,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useFetchMyItemListQuery = (fetchMyItemListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchMyItemListParams) {
    t0 = itemQueryKeys.fetchMyItemList(fetchMyItemListParams);
    $[0] = fetchMyItemListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchMyItemListParams) {
    t1 = () => fetchMyItemList(fetchMyItemListParams);
    $[2] = fetchMyItemListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFetchMyItemListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = itemQueryKeys.fetchMyItemList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchMyItemList({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp5,
      select: _temp7
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useSendItemGiftMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = itemQueryKeys.fetchSendItemGift();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp8,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useSendPushMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = itemQueryKeys.fetchSendPush();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp9,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useFetchItemDetailQuery = (fetchMyItemListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchMyItemListParams) {
    t0 = itemQueryKeys.fetchItemDetail(fetchMyItemListParams);
    $[0] = fetchMyItemListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchMyItemListParams) {
    t1 = () => fetchItemDetail(fetchMyItemListParams);
    $[2] = fetchMyItemListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFetchItemDetailInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = itemQueryKeys.fetchItemDetail(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchItemDetail({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp11,
      select: _temp13
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
function _temp() {
  return fetchItemStoreItemList();
}
function _temp2() {
  return fetchGiftableItemList();
}
function _temp3(postUseItemParams) {
  return fetchItemUse(postUseItemParams);
}
function _temp4(page) {
  return page.list;
}
function _temp5(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp4).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp6(page_0) {
  return page_0.list ?? [];
}
function _temp7(data) {
  return {
    pages: data?.pages.flatMap(_temp6),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp8(params) {
  return fetchSendItemGift(params);
}
function _temp9(params) {
  return fetchSendPush(params);
}
function _temp10(page) {
  return page.list;
}
function _temp11(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp10).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp12(page_0) {
  return page_0.list ?? [];
}
function _temp13(data) {
  return {
    pages: data?.pages.flatMap(_temp12),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
    func: data.pages[0].func || ""
  };
}